import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Calendar, CheckCircle2, Circle, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  follow_up: ArrowRight,
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const isOverdue = d < new Date(now.setHours(0, 0, 0, 0));
  return {
    label: isToday ? `Heute · ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
      : d.toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
    overdue: isOverdue && !isToday,
    today: isToday,
  };
}

export default function TodayView({ onLeadClick }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tasks/today?include_overdue=true`, { withCredentials: true });
      setTasks(r.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const handleToggle = async (t) => {
    try {
      await axios.put(`${API}/tasks/${t.id}`, { completed: !t.completed }, { withCredentials: true });
      toast.success(t.completed ? "Wieder offen" : "Erledigt!");
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  const overdue = tasks.filter(t => { const d = fmtDate(t.due_at); return d.overdue; });
  const today = tasks.filter(t => { const d = fmtDate(t.due_at); return d.today || (!d.overdue && !d.today); });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="today-view">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Mein Tag</p>
        <h2 className="font-heading text-xl font-black tracking-tight">Was steht heute an?</h2>
        <p className="text-sm text-muted-foreground mt-1">{tasks.length} {tasks.length === 1 ? "offene Aufgabe" : "offene Aufgaben"}</p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Lade...</p>}

      {!loading && tasks.length === 0 && (
        <div className="text-center py-12 border border-dashed border-border rounded-sm">
          <CheckCircle2 size={32} className="mx-auto text-emerald-600 mb-3" />
          <p className="text-sm font-medium text-foreground">Alles erledigt!</p>
          <p className="text-xs text-muted-foreground mt-1">Kein offener Task für heute. Zeit für neue Akquise!</p>
        </div>
      )}

      {overdue.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest font-bold text-destructive">Überfällig ({overdue.length})</p>
          {overdue.map(t => <TaskRow key={t.id} task={t} onToggle={handleToggle} onLeadClick={onLeadClick} variant="overdue" />)}
        </div>
      )}

      {today.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Heute</p>
          {today.map(t => <TaskRow key={t.id} task={t} onToggle={handleToggle} onLeadClick={onLeadClick} variant="today" />)}
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onLeadClick, variant }) {
  const Icon = TYPE_ICON[task.type] || ArrowRight;
  const d = fmtDate(task.due_at);
  return (
    <div className={`flex items-start gap-3 p-3 border rounded-sm bg-card group ${variant === "overdue" ? "border-destructive/30" : "border-border"}`} data-testid={`today-task-${task.id}`}>
      <button onClick={() => onToggle(task)} className="mt-0.5 shrink-0">
        {task.completed ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} className="text-muted-foreground hover:text-foreground" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <Icon size={11} className="text-muted-foreground" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{task.type}</span>
          <span className={`text-[10px] font-mono ${variant === "overdue" ? "text-destructive font-bold" : "text-muted-foreground"}`}>{d.label}</span>
        </div>
        <p className="text-sm font-medium text-foreground">{task.title}</p>
        {task.lead && (
          <button
            onClick={() => onLeadClick?.(task.lead_id)}
            className="text-xs text-muted-foreground hover:text-foreground hover:underline mt-1 inline-flex items-center gap-1"
            data-testid={`open-lead-${task.lead_id}`}
          >
            {task.lead.company_name} · {task.lead.contact_name}
            {task.lead.contact_phone && <a href={`tel:${task.lead.contact_phone}`} onClick={e => e.stopPropagation()} className="ml-1 font-mono">→ {task.lead.contact_phone}</a>}
          </button>
        )}
      </div>
    </div>
  );
}
